/**
 * @file HazeGroup.tsx
 * @description Группа «Свечение» вкладки «Фон и рамка» (phase19.16, SB89;
 * партия SB120, этап 2 — uiux-sb120-stage2 §2, §5; данные
 * db-design-sb120-stage2 §1 `backdrop.haze {strength 0–50, color?}`):
 *
 * ```
 * Свечение          18 % · акцент                      ⌃
 *  Сила   ├───●──────┤ [ 18 ] %   [Нет 0][Мягкое 18][Сильное 35]
 *  Цвет   [Акцент ✓][Фон страницы]…[Кнопки]…[⌖ Свой]
 * ```
 *
 * Вместо прежнего флажка «Мягкое цветное свечение» — сила %, 18 — прежняя
 * дымка. Цвет: нет ключа — акцент (первая ячейка), любая из 9 ролей или
 * свой. Сила 0 — свечения нет, цвет серый не прячется: пояснение на месте.
 */

import { SITE_COLOR_ROLES, SITE_THEME_LIMITS, type SiteTheme } from "@chaiberi/shared";

import { FieldHint, SettingsGroup } from "../../ui/settings-groups";
import { ColorRefField } from "../../site-fields/ColorRefField";
import { NumberSliderField } from "../../site-fields/NumberSliderField";
import { HAZE_PRESETS, HAZE_RECOMMENDED, outOfRecommended } from "../../../lib/site-view/appearance-numbers";
import { hazeSummary } from "../../../lib/site-view/backdrop-summary";

type Haze = SiteTheme["backdrop"]["haze"];

const { hazeStrength: HS } = SITE_THEME_LIMITS;
/** Акцент — первая ячейка «как у сайта», среди ролей его не повторяем. */
const HAZE_ROLES = SITE_COLOR_ROLES.filter((role) => role !== "accent");

interface Props {
  haze: Haze;
  colors: SiteTheme["colors"];
  onHaze: (haze: Haze) => void;
  onPreview?: (haze: Haze) => void;
}

export function HazeGroup({ haze, colors, onHaze, onPreview }: Props) {
  const withColor = (color: Haze["color"]): Haze => {
    const next: Haze = { ...haze, color };
    // Нет цвета — акцент: ключ снимается.
    if (color === undefined) delete next.color;
    return next;
  };

  // Явная роль «Акцент» в данных — та же первая ячейка «Акцент» (иначе ни одна не отмечена).
  const shown = haze.color?.kind === "role" && haze.color.role === "accent" ? undefined : haze.color;

  return (
    <SettingsGroup
      id="haze"
      title="Свечение"
      hint="Мягкие пятна цвета по углам страницы под блоками."
      summary={hazeSummary(haze)}
      status={outOfRecommended(haze.strength, HAZE_RECOMMENDED) ? "warning" : undefined}
    >
      <NumberSliderField
        label="Сила свечения"
        value={haze.strength}
        inherit={{ value: HS.min, label: "нет" }}
        min={HS.min}
        max={HS.max}
        step={1}
        unit="%"
        presets={HAZE_PRESETS}
        recommended={HAZE_RECOMMENDED}
        hint={`${HS.legacy} — прежнее «мягкое свечение».`}
        onChange={(v) => onHaze({ ...haze, strength: v ?? HS.min })}
        onPreview={onPreview ? (v) => onPreview({ ...haze, strength: v ?? HS.min }) : undefined}
      />
      <ColorRefField label="Цвет свечения" value={shown} onChange={(color) => onHaze(withColor(color))} colors={colors} autoLabel="Акцент" roles={HAZE_ROLES} />
      {haze.strength <= 0 ? <FieldHint className="mt-0">Цвет заметен, когда сила больше 0.</FieldHint> : null}
    </SettingsGroup>
  );
}
